<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { platformGet } from "@/common/api/rest/platform";
import { groupList, groupAdd, groupDel } from "@/common/api/rest/group";

import type { PlatformType } from "@/common/types/platform";
import type { GroupType, GroupPayloadType } from "@/common/types/group";

import { FormGroup } from "@/components";
import {
  breadcrumbSimple,
  baseConfirm,
  baseModal,
  baseTable,
  iconMenu,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const platformData = ref<PlatformType | null>(null);

const tableData = ref<GroupType[]>([]);
const payload = ref<GroupPayloadType | null>(null);

const selected = ref<GroupType | null>(null);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);

const platformId = computed(() => route.params.platform as string);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
  { key: "slug", label: t("common.labels.slug") },
]);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "platforms",
    label: t("common.labels.platforms"),
    url: `/register/platform`,
  },
  {
    key: "platform",
    label: platformData.value?.name ?? "...",
    url: `/register/platform/${platformId.value}/edit`,
  },
  {
    key: "groups",
    label: t("group.labels.groups"),
    url: `/register/platform/${platformId.value}/group`,
  },
]);

const menuList = computed(() => [
  {
    key: "platform",
    icon: "home-line",
    label: t("platform.labels.platform"),
    url: `/register/platform/${platformId.value}/edit`,
  },
  {
    key: "group",
    icon: "field",
    label: t("group.labels.groups"),
    url: `/register/platform/${platformId.value}/group`,
  },
]);

async function fetchPlatform() {
  await platformGet(platformId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        platformData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchData() {
  await groupList(platformId.value, {
    page: page.value,
    limit: limit.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function changeLimit(value: number) {
  limit.value = value;
  page.value = 0;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;

  fetchData();
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  groupAdd(platformId.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        editItem(data);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: GroupType) {
  router.push(
    `/register/platform/${data.platform_id}/group/${data.group_id}/edit`
  );
}

function removeItem(data: GroupType) {
  selected.value = data;
  showModalDel.value = true;
}

function removeItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function removeItemConfirm() {
  if (!selected.value) return;

  groupDel(selected.value.group_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeMenu(url: string) {
  router.push(url);
}

function back() {
  router.push("/register/platform");
}

onMounted(async () => {
  await fetchPlatform();
  await fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="groups"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="platform" size="2rem" />
          {{ t("platform.labels.edit_platform") }}
        </div>

        <div class="actions">
          <button class="btn btn-default" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <icon-menu
          :tabs="menuList"
          active="group"
          size="3rem"
          @selected="changeMenu($event)"
        />

        <base-table
          :header="tableHeader"
          :data="tableData"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :del="true"
          :edit="true"
          :show-limit="true"
          @change:page="changePage($event)"
          @change:limit="changeLimit($event)"
          @searching="searchItem($event)"
          @btn:add="addItem()"
          @btn:edit="editItem($event)"
          @btn:del="removeItem($event)"
        >
          <template #status="{ data }">
            <template v-if="data.status === 'ACTIVE'">
              {{ t("common.labels.active") }}
            </template>
            <template v-else>
              {{ t("common.labels.inactive") }}
            </template>
          </template>
        </base-table>
      </div>
    </section>
  </article>

  <base-modal
    :show="showModalAdd"
    :title="t('group.modals.titles.add_group').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="addItemCancel()"
    @confirm="addItemConfirm()"
  >
    <template #content>
      <form-group
        @update:data="payload = $event"
        @validate="payloadValid = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalDel"
    :title="t('group.modals.titles.del_group').toUpperCase()"
    :message="
      t('group.modals.messages.del_group', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeItemCancel()"
    @confirm="removeItemConfirm()"
  />
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
